/**
 * Allowlists de user_id para funcionalidades gated.
 *
 * Molde: WA_SIGNUP_PREVIEW_USER_IDS em ConfigSDR.tsx (#190/#10.5).
 * Centralizado aqui para compartilhar entre rotas/paginas.
 */

import { mensalidadeNoArParaPapel } from "@/lib/mensalidade/liberacao"

/**
 * #223: usuarios que podem acessar a pagina de gerenciamento de ESCs
 * e a Calculadora de Preco SEM serem super_admin.
 *
 * Michel Nassif (mpznassif@gmail.com) — socio da Invenia, responsavel
 * final pelo produto. Precisa ver o estado das unidades e simular
 * precos, mas NAO pode ver PII de leads, dados de rede ou faturamento.
 */
export const ESC_MANAGEMENT_ALLOWED_USER_IDS = [
  "782a2fe0-f7cc-4aad-a869-de074b988c66", // Michel (mpznassif@gmail.com)
]

/**
 * Verifica se um user_id esta na allowlist OU e super_admin.
 */
export function canAccessESCManagement(
  userId: string | undefined,
  role: string | undefined,
): boolean {
  if (role === "super_admin") return true
  if (!userId) return false
  return ESC_MANAGEMENT_ALLOWED_USER_IDS.includes(userId)
}

// ─── O Gerenciamento e as abas dele (D-B1, bloco B da mensalidade) ───────────


export type AbaDoGerenciamento = "unidades" | "calculadora" | "cobrancas"

/**
 * A aba "Cobranças": quem opera a cobrança da mensalidade da rede, e só eles
 * (decisão do PM de 30/09/2026: `super_admin` e `network_manager`), com as
 * telas da mensalidade no ar para o papel deles (`lib/mensalidade/liberacao.ts`,
 * o lugar único da trava — no ensaio para todos, fora dele só para quem opera,
 * pedido do PM de 07/10/2026). O servidor confere o papel de novo e recusa o
 * resto com 403.
 */
export function podeVerAsCobrancas(role: string | undefined): boolean {
  return (role === "super_admin" || role === "network_manager") && mensalidadeNoArParaPapel(role)
}

/**
 * As abas que a pessoa vê, na ordem da tela. "Unidades" e "Calculadora de
 * Preços" continuam pela régua de sempre (`canAccessESCManagement`); a
 * "Cobranças" pela dela. O `network_manager` fora da lista de autorizados vê
 * só "Cobranças" e, fora do ensaio, nada.
 */
export function abasDoGerenciamento(userId: string | undefined, role: string | undefined): AbaDoGerenciamento[] {
  const abas: AbaDoGerenciamento[] = []
  if (canAccessESCManagement(userId, role)) abas.push("unidades", "calculadora")
  if (podeVerAsCobrancas(role)) abas.push("cobrancas")
  return abas
}

/** A rota e o item "Gerenciamento" do menu abrem para quem vê ao menos uma aba. */
export function podeEntrarNoGerenciamento(userId: string | undefined, role: string | undefined): boolean {
  return abasDoGerenciamento(userId, role).length > 0
}

/** A aba aberta: a pedida no endereço (`?aba=`), se a pessoa a vê; senão, a primeira que ela vê. */
export function abaEscolhida(pedida: string | null, abas: readonly AbaDoGerenciamento[]): AbaDoGerenciamento | null {
  return pedida !== null && (abas as readonly string[]).includes(pedida) ? (pedida as AbaDoGerenciamento) : (abas[0] ?? null)
}
